import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme, Switch, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreatePage } from "../../hooks/pages/useCreatePage";
import { useUpdatePage } from "../../hooks/pages/useUpdatePage";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditPage = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [contentAr, setContentAr] = useState("");
    const [contentEn, setContentEn] = useState("");
    const [slug, setSlug] = useState("");
    const [isActive, setIsActive] = useState(true);

    // SEO States
    // const [metaTitle, setMetaTitle] = useState("");
    // const [metaDescription, setMetaDescription] = useState("");
    // const [keywords, setKeywords] = useState("");

    // Hooks
    const { createPageMutate, isLoading: isCreating } = useCreatePage();
    const { editPage, isUpdating } = useUpdatePage();

    const loadingSubmit = isCreating || isUpdating;

    // Auto-slug generation from English title
    useEffect(() => {
        if (titleEn) {
            setSlug(slugify(titleEn));
        }
    }, [titleEn]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setContentAr(currentData?.content?.ar || "");
            setContentEn(currentData?.content?.en || "");
            setSlug(currentData.slug || "");
            setIsActive(currentData.is_published ?? true);
            // setMetaTitle(currentData.meta_title || "");
            // setMetaDescription(currentData.meta_description || "");
            // setKeywords(currentData.keywords || "");
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                en: titleEn || titleAr,
                ar: titleAr || titleEn
            },
            content: {
                en: contentEn || contentAr,
                ar: contentAr || contentEn
            },
            slug,
            is_published: isActive,
            // meta_title: metaTitle,
            // meta_description: metaDescription,
            // keywords: keywords
        };

        if (isEdit && currentData?.id) {
            editPage(
                { id: currentData.id, data: payload },
                { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
            );
        } else {
            createPageMutate(payload, {
                onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); }
            });
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit ? (isArabic ? "تعديل الصفحة" : "Edit Page") : (isArabic ? "إضافة صفحة جديدة" : "Create New Page")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isArabic ? "قم بتعبئة محتوى الصفحة وإعدادات SEO" : "Fill in the page content and SEO settings"}
                        </Typography>
                    </Box>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={4}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "البيانات الأساسية" : "Basic Information"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {activeLang === "ar" ? (
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="مثلاً: من نحن"
                                type="text"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                            />
                        ) : (
                            <FormInput
                                label="Title (English)"
                                placeholder="e.g., About Us"
                                type="text"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                            />
                        )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug)" : "Slug"}
                            placeholder="about-us"
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            required
                            disabled={isEdit}
                        />
                    </Grid>

                    {/* Content Section */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "محتوى الصفحة" : "Page Content"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                        {activeLang === "ar" ? (
                            <FormInput
                                label="المحتوى (عربي)"
                                type="editor"
                                value={contentAr}
                                onChange={(e) => setContentAr(e.target.value)}
                                required
                            />
                        ) : (
                            <FormInput
                                label="Content (English)"
                                type="editor"
                                value={contentEn}
                                onChange={(e) => setContentEn(e.target.value)}
                                required
                            />
                        )}
                    </Grid>

                    {/* Settings Section */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "الإعدادات" : "Settings"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.5,
                            border: '1px solid',
                            borderColor: theme => theme.palette.divider,
                            borderRadius: '12px',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                        }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {isActive ? (isArabic ? "هذه الصفحة ستظهر للعملاء" : "Visible to customers") : (isArabic ? "هذه الصفحة مخفية حالياً" : "Hidden from customers")}
                                </Typography>
                            </Box>
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                color="primary"
                            />
                        </Box>
                    </Grid>

                    {/* SEO Section */}
                    {/* <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "تحسين محركات البحث (SEO)" : "SEO Settings"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "عنوان SEO" : "Meta Title"}
                            placeholder="Meta Title"
                            type="text"
                            value={metaTitle}
                            onChange={(e) => setMetaTitle(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الكلمات المفتاحية" : "Keywords"}
                            placeholder="e.g., store, shipping, policy"
                            type="text"
                            value={keywords}
                            onChange={(e) => setKeywords(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "وصف SEO" : "Meta Description"}
                            placeholder="Meta Description..."
                            type="textarea"
                            value={metaDescription}
                            onChange={(e) => setMetaDescription(e.target.value)}
                            textareaRows="100px"
                        />
                    </Grid> */}
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loadingSubmit}
                        sx={{
                            minWidth: 160,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث الصفحة" : "Update Page") : (isArabic ? "إنشاء الصفحة" : "Create Page"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditPage;
